/**
 * GA4 E-Commerce Event Tracking Utilities
 *
 * Fires standard GA4 e-commerce events when gtag is loaded and consent is granted.
 * All functions are safe to call even if gtag is not available.
 */

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

interface GA4Item {
  item_id: string;
  item_name: string;
  item_category?: string;
  price?: number;
  quantity?: number;
  item_variant?: string;
  item_brand?: string;
}

function isGtagAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
}

function buildItem(product: {
  product_id?: number;
  productId?: number;
  name?: string;
  slug?: string;
  category_name?: string;
  categoryName?: string;
  base_price?: number | string;
  unit_price?: number | string;
  price?: number | string;
  quantity?: number;
  vendor_name?: string;
  vendorName?: string;
  fabricName?: string;
}): GA4Item {
  return {
    item_id: String(product.product_id || product.productId || ''),
    item_name: product.name || product.slug || '',
    item_category: product.category_name || product.categoryName || '',
    price: parseFloat(String(product.unit_price || product.base_price || product.price || 0)),
    quantity: product.quantity || 1,
    item_brand: product.vendor_name || product.vendorName || '',
    item_variant: product.fabricName || '',
  };
}

/** Fires when a customer views a product configurator page */
export function trackViewItem(product: Record<string, any>) {
  if (!isGtagAvailable()) return;
  const item = buildItem(product);
  window.gtag!('event', 'view_item', {
    currency: 'USD',
    value: item.price || 0,
    items: [item],
  });
}

/** Fires when an item is added to cart */
export function trackAddToCart(product: Record<string, any>, quantity: number = 1) {
  if (!isGtagAvailable()) return;
  const item = buildItem({ ...product, quantity });
  window.gtag!('event', 'add_to_cart', {
    currency: 'USD',
    value: (item.price || 0) * quantity,
    items: [item],
  });
}

/** Fires when an item is removed from cart */
export function trackRemoveFromCart(product: Record<string, any>) {
  if (!isGtagAvailable()) return;
  const item = buildItem(product);
  window.gtag!('event', 'remove_from_cart', {
    currency: 'USD',
    value: item.price || 0,
    items: [item],
  });
}

/** Fires when the cart page is viewed */
export function trackViewCart(cartItems: Record<string, any>[], totalValue: number) {
  if (!isGtagAvailable()) return;
  const items = cartItems.map(item => buildItem(item));
  window.gtag!('event', 'view_cart', {
    currency: 'USD',
    value: totalValue,
    items,
  });
}

/** Fires when checkout begins */
export function trackBeginCheckout(cartItems: Record<string, any>[], totalValue: number) {
  if (!isGtagAvailable()) return;
  const items = cartItems.map(item => buildItem(item));
  window.gtag!('event', 'begin_checkout', {
    currency: 'USD',
    value: totalValue,
    items,
  });
}

/** Fires on successful purchase.
 *  Splits Google Ads conversion: sample-only orders route to a separate conversion
 *  label (or skip Ads conversion entirely if no label configured) so $0 sample
 *  orders don't pollute smart-bidding data for paid purchases. GA4 purchase
 *  event always fires for ecommerce reporting. */
export function trackPurchase(order: {
  orderNumber?: string;
  order_number?: string;
  total?: number;
  total_amount?: number;
  subtotal?: number;
  tax?: number;
  tax_amount?: number;
  shipping?: number;
  shipping_amount?: number;
  coupon?: string;
  items?: Record<string, any>[];
  isSampleOnly?: boolean;
}) {
  if (!isGtagAvailable()) return;
  const transactionId = order.orderNumber || order.order_number || '';
  const value = order.total || order.total_amount || 0;
  const items = (order.items || []).map(item => buildItem(item));

  // GA4 purchase event (feeds GA4 reporting and GA4-imported Google Ads conversions)
  window.gtag!('event', 'purchase', {
    transaction_id: transactionId,
    value,
    currency: 'USD',
    tax: order.tax || order.tax_amount || 0,
    shipping: order.shipping || order.shipping_amount || 0,
    coupon: order.coupon || '',
    items,
  });

  // Google Ads conversion: route sample-only orders to a separate conversion
  // action so they don't train smart bidding with $0 values. Labels are
  // hardcoded — they're public (visible in the gtag snippet anyway) and we
  // build locally, so NEXT_PUBLIC_* env vars can't be set per environment.
  const PURCHASE_LABEL = 'AW-18131246977/badWCOOVqq8cEIG_08VD';
  const SAMPLE_LABEL = 'AW-18131246977/z3w4CKew9rMcEIG_08VD';

  const sendTo = order.isSampleOnly ? SAMPLE_LABEL : PURCHASE_LABEL;
  window.gtag!('event', 'conversion', {
    send_to: sendTo,
    value,
    currency: 'USD',
    transaction_id: transactionId,
  });
}

/** Fires when a search is performed */
export function trackSearch(searchTerm: string) {
  if (!isGtagAvailable()) return;
  window.gtag!('event', 'search', {
    search_term: searchTerm,
  });
}

/** Fires when product listing is viewed */
export function trackViewItemList(listName: string, products: Record<string, any>[]) {
  if (!isGtagAvailable()) return;
  const items = products.slice(0, 20).map((product, index) => ({
    ...buildItem(product),
    index,
  }));
  window.gtag!('event', 'view_item_list', {
    item_list_name: listName,
    items,
  });
}
